/**
 * TTSGenerator — admin-only component that converts a block of text
 * into audio via the platform TTS proxy (/api/tts/*).
 *
 * Props:
 *   • text       — string. The content to vocalise.
 *   • contentId  — string. Saved as `audio_url` on this content record
 *                 server-side once generation succeeds.
 *   • onGenerated(url) — optional callback fired with the resulting URL.
 *
 * The component fetches the available voices on mount and renders:
 *   • Voice dropdown (from /api/tts/voices, default fallback to "default")
 *   • Language dropdown (en, es, fr, de, pt, it, zh, ja, ko)
 *   • "Generate audio" button with loading spinner
 *   • Inline <audio> preview + copyable URL field on success
 *   • Red error box on failure
 *
 * Visual language matches the dark slate theme used everywhere else.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Loader2, AudioWaveform, Copy, Check, AlertTriangle, Mic2 } from 'lucide-react';
import { toast } from 'sonner';
import { authFetch } from '../utils/authUtils';
import VoicePreviewButton from './VoicePreviewButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Spanish' },
  { code: 'fr', label: 'French' },
  { code: 'de', label: 'German' },
  { code: 'pt', label: 'Portuguese' },
  { code: 'it', label: 'Italian' },
  { code: 'zh', label: 'Chinese' },
  { code: 'ja', label: 'Japanese' },
  { code: 'ko', label: 'Korean' },
];

const TTSGenerator = ({ text, contentId, onGenerated }) => {
  const [voices, setVoices] = useState(['default']);
  const [voice, setVoice] = useState('default');
  const [language, setLanguage] = useState('en');
  const [busy, setBusy] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [upstreamOffline, setUpstreamOffline] = useState(false);
  const urlInputRef = useRef(null);

  // Fetch available voices once. Fallback to ["default"] on failure so
  // the dropdown always has at least one option to render.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/api/tts/voices`);
        if (!r.ok) return;
        const d = await r.json();
        if (cancelled) return;
        const list = Array.isArray(d.voices) ? d.voices : ['default'];
        setVoices(list.length ? list : ['default']);
        setUpstreamOffline(Boolean(d.upstream_offline));
      } catch { /* keep ["default"] fallback */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const trimmed = (text || '').trim();
  const charCount = trimmed.length;
  const wordCount = trimmed ? trimmed.split(/\s+/).length : 0;
  const canGenerate = !busy && charCount > 0;

  const generate = async () => {
    if (!canGenerate) return;
    setBusy(true);
    setError(null);
    setAudioUrl(null);
    setCopied(false);
    try {
      // Submit the job. New async upstream returns `{job_id, status:"queued"}`
      // immediately; legacy sync upstream returns the audio URL inline.
      const r = await authFetch(`${BACKEND_URL}/api/tts/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: trimmed,
          voice,
          language,
          content_id: contentId || '',
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        throw new Error(data.detail || data.error || `HTTP ${r.status}`);
      }

      let finalUrl = data.url || data.audio_url;

      // Async path — poll /status until done or 5 min timeout.
      if (!finalUrl && data.job_id) {
        const jobId = data.job_id;
        const deadline = Date.now() + 5 * 60 * 1000;
        while (Date.now() < deadline) {
          await new Promise((rs) => setTimeout(rs, 2000));
          let pollRes;
          try {
            pollRes = await authFetch(`${BACKEND_URL}/api/tts/status/${jobId}`);
          } catch {
            continue; // transient — keep polling
          }
          const pollData = await pollRes.json().catch(() => ({}));
          if (!pollRes.ok) {
            throw new Error(pollData.detail || pollData.error || `Poll HTTP ${pollRes.status}`);
          }
          const state = pollData.status;
          if (state === 'done') {
            finalUrl = pollData.url || pollData.audio_url;
            break;
          }
          if (state === 'error') {
            throw new Error(pollData.error || 'TTS job failed');
          }
          // queued / running / progress → keep polling.
        }
        if (!finalUrl) throw new Error('TTS generation timed out');
      }

      if (!finalUrl) throw new Error('TTS response did not include an audio URL');
      setAudioUrl(finalUrl);
      onGenerated?.(finalUrl);
      toast.success('Audio generated');
    } catch (err) {
      setError(err.message || 'TTS request failed');
    } finally {
      setBusy(false);
    }
  };

  const copyUrl = async () => {
    if (!audioUrl) return;
    try {
      await navigator.clipboard.writeText(audioUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Fallback for browsers without clipboard API.
      urlInputRef.current?.select();
      try { document.execCommand('copy'); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* ignore */ }
    }
  };

  return (
    <div
      className="rounded-xl border border-slate-700 bg-slate-900/60 p-4 space-y-3"
      data-testid="tts-generator"
    >
      <header className="flex items-center gap-2">
        <AudioWaveform className="w-4 h-4 text-indigo-300" />
        <h3 className="text-sm font-bold text-white tracking-wide">
          Generate Audio from Text
        </h3>
        <span className="ml-auto text-[10px] uppercase tracking-widest font-bold text-slate-500">
          TTS · Admin
        </span>
      </header>

      <p className="text-[11px] text-slate-400" data-testid="tts-word-count">
        {charCount > 0
          ? `${wordCount.toLocaleString()} word${wordCount === 1 ? '' : 's'} · ${charCount.toLocaleString()} character${charCount === 1 ? '' : 's'} ready to vocalise.`
          : 'Add some text above to enable generation.'}
      </p>

      {upstreamOffline && (
        <div
          className="rounded-md border border-amber-500/60 bg-amber-950/40 px-3 py-2 text-xs text-amber-200 flex items-start gap-2"
          data-testid="tts-upstream-offline"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="space-y-1">
            <p className="font-bold">Your TTS server is offline.</p>
            <p className="text-amber-200/80">
              The Cloudflare quick tunnel (<code className="font-mono">trycloudflare.com</code>) URL no longer resolves — it dies whenever your <code className="font-mono">cloudflared</code> process stops. Restart the tunnel and update <code className="font-mono">TTS_SERVICE_URL</code> in <code className="font-mono">backend/.env</code> with the new URL. Only "default" voice will be available until then.
            </p>
          </div>
        </div>
      )}

      {/* Voice picker — explicit card pills instead of a native <select>
          so long voice names (e.g. "The Antagonist 1") are fully visible
          without forcing the user to open a dropdown to discover them.
          The active voice is clearly outlined and gets a checkmark. */}
      <div>
        <div className="flex items-baseline justify-between mb-2">
          <span className="text-[10px] uppercase tracking-widest font-bold text-slate-400 inline-flex items-center gap-1.5">
            <Mic2 className="w-3 h-3 text-indigo-300" />
            Voice
            <span className="text-slate-500 normal-case font-normal tracking-normal">
              ({voices.length} available)
            </span>
          </span>
          <span className="text-[10px] font-mono text-indigo-300 truncate max-w-[60%] text-right">
            {(typeof voice === 'string' ? voice : (voice?.name || voice?.id)) || 'default'}
          </span>
        </div>
        <div
          className="flex flex-wrap gap-1.5"
          role="radiogroup"
          aria-label="Voice"
          data-testid="tts-voice-picker"
        >
          {voices.map((v) => {
            const val = typeof v === 'string' ? v : (v.id || v.name || 'default');
            const label = typeof v === 'string' ? v : (v.name || v.id || 'default');
            const active = val === voice;
            return (
              <div
                key={val}
                className={`inline-flex items-center gap-1.5 rounded-full border transition ${
                  active
                    ? 'bg-indigo-500/25 border-indigo-300 shadow-[0_0_0_1px_rgba(99,102,241,0.35),0_8px_24px_-12px_rgba(99,102,241,0.6)]'
                    : 'bg-slate-800 border-slate-700 hover:border-indigo-400/60'
                }`}
              >
                <button
                  type="button"
                  role="radio"
                  aria-checked={active}
                  disabled={busy}
                  onClick={() => setVoice(val)}
                  className={`inline-flex items-center gap-1.5 pl-3 pr-1 py-1.5 text-xs font-bold rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300 disabled:opacity-50 ${
                    active ? 'text-white' : 'text-slate-300 hover:text-white'
                  }`}
                  data-testid={`tts-voice-option-${val.replace(/\s+/g, '-')}`}
                >
                  {active && <Check className="w-3 h-3 text-indigo-200" />}
                  <span className="truncate max-w-[200px]">{label}</span>
                </button>
                <VoicePreviewButton voice={val} size="xs" className="mr-1" />
              </div>
            );
          })}
        </div>
        {/* Hidden native <select> kept for legacy tests / accessibility
            tools that still address the original testid. Mirrors the
            visible-pill state in both directions. */}
        <select
          value={voice}
          onChange={(e) => setVoice(e.target.value)}
          disabled={busy}
          className="sr-only"
          data-testid="tts-voice-select"
          aria-hidden="true"
          tabIndex={-1}
        >
          {voices.map((v) => {
            const val = typeof v === 'string' ? v : (v.id || v.name || 'default');
            return <option key={val} value={val}>{val}</option>;
          })}
        </select>
      </div>

      <div>
        <span className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-1 block">Language</span>
        <select
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          disabled={busy}
          className="w-full rounded-md bg-slate-800 border border-slate-700 text-white text-sm px-2.5 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
          data-testid="tts-language-select"
        >
          {LANGUAGES.map((l) => (
            <option key={l.code} value={l.code}>{l.label}</option>
          ))}
        </select>
      </div>

      <button
        type="button"
        onClick={generate}
        disabled={!canGenerate}
        className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-bold px-4 py-2.5 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300 transition-colors"
        data-testid="tts-generate-btn"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <AudioWaveform className="w-4 h-4" />}
        {busy ? 'Generating…' : 'Generate Audio'}
      </button>

      {error && (
        <div
          className="rounded-md border border-rose-500/60 bg-rose-950/40 px-3 py-2 text-xs text-rose-200 flex items-start gap-2"
          data-testid="tts-error"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span className="break-words">{error}</span>
        </div>
      )}

      {audioUrl && (
        <div className="space-y-2" data-testid="tts-result">
          <audio
            controls
            src={audioUrl}
            className="w-full"
            preload="none"
            data-testid="tts-audio-player"
          >
            Your browser doesn’t support inline audio playback.
          </audio>
          <div className="flex items-center gap-2">
            <input
              ref={urlInputRef}
              readOnly
              value={audioUrl}
              onFocus={(e) => e.target.select()}
              className="flex-1 min-w-0 rounded-md bg-slate-800 border border-slate-700 text-slate-200 text-xs px-2.5 py-2 font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
              data-testid="tts-url-input"
            />
            <button
              type="button"
              onClick={copyUrl}
              className="shrink-0 inline-flex items-center gap-1.5 rounded-md bg-slate-700 hover:bg-slate-600 text-white text-xs font-bold px-3 py-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300"
              data-testid="tts-copy-url"
            >
              {copied ? <Check className="w-3.5 h-3.5 text-emerald-300" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TTSGenerator;
